// @ts-check
// Rosca em SVG próprio (doc 16, 10.7) com métrica central opcional e legenda com valores e participação.
// Mais de seis fatias: as menores viram "Outros", em cinza, para a cor nunca repetir. A regra das fatias dobradas é a
// mesma do servidor (foldSliceKeys), para o clique em "Outros" abrir exatamente o que foi somado (contrato 07, seção 10).
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { ChartTooltip, OTHER_SERIES_COLOR, SERIES_COLOR_COUNT, getSeriesColor, truncateToWidth, useElementSize } from './chart-utils.js';
import { formatMetricValue } from './widget-format.js';
import { handleDrillLinkClick, useChartPointNavigation, useDrilldown } from './drilldown.js';
import { OTHER_SLICE_KEY, foldSliceKeys } from '../../../../shared/dashboards/fold-slices.js';
import { formatNumber } from '../../lib/format.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/dashboards.js').PieWidgetData} PieWidgetData
 */

/**
 * @param {PieWidgetData['slices']} slices
 */
export function foldSlices(slices) {
  const { keptKeys, otherKeys } = foldSliceKeys(slices, SERIES_COLOR_COUNT);
  const byKey = new Map(slices.map((slice) => [slice.key, slice]));
  // a cor segue a posição original da fatia, não o tamanho, para não repintar ao mudar o filtro
  const kept = keptKeys.map((key, index) => ({ .../** @type {PieWidgetData['slices'][number]} */ (byKey.get(key)), color: getSeriesColor(index) }));
  if (otherKeys.length === 0) return kept;
  const otherValue = otherKeys.reduce((sum, key) => sum + Number(byKey.get(key)?.value ?? 0), 0);
  return [...kept, { key: OTHER_SLICE_KEY, label: t('dashboards.chart.other'), value: otherValue, color: OTHER_SERIES_COLOR }];
}

/**
 * @param {number} centerX
 * @param {number} centerY
 * @param {number} radius
 * @param {number} angle
 */
function polar(centerX, centerY, radius, angle) {
  return [centerX + radius * Math.sin(angle), centerY - radius * Math.cos(angle)];
}

/**
 * @param {number} centerX
 * @param {number} centerY
 * @param {number} outerRadius
 * @param {number} innerRadius
 * @param {number} startAngle
 * @param {number} endAngle
 */
function buildArcPath(centerX, centerY, outerRadius, innerRadius, startAngle, endAngle) {
  // um círculo inteiro não fecha com um único arco
  const end = endAngle - startAngle >= Math.PI * 2 ? startAngle + Math.PI * 2 - 0.0001 : endAngle;
  const largeArc = end - startAngle > Math.PI ? 1 : 0;
  const [outerStartX, outerStartY] = polar(centerX, centerY, outerRadius, startAngle);
  const [outerEndX, outerEndY] = polar(centerX, centerY, outerRadius, end);
  const [innerEndX, innerEndY] = polar(centerX, centerY, innerRadius, end);
  const [innerStartX, innerStartY] = polar(centerX, centerY, innerRadius, startAngle);
  return `M${outerStartX},${outerStartY}A${outerRadius},${outerRadius} 0 ${largeArc} 1 ${outerEndX},${outerEndY}L${innerEndX},${innerEndY}A${innerRadius},${innerRadius} 0 ${largeArc} 0 ${innerStartX},${innerStartY}Z`;
}

/**
 * @param {{ data: PieWidgetData, showCenterMetric?: boolean, showDataLabels?: boolean }} props
 */
export function PieChart({ data, showCenterMetric = true, showDataLabels = false }) {
  const [containerRef, size] = useElementSize();
  const [hover, setHover] = useState(/** @type {{ key: string, x: number, y: number, source?: 'pointer' | 'keyboard' | 'touch', href?: string | null } | null} */ (null));
  const drill = useDrilldown();
  const slices = foldSlices(data.slices);
  const total = slices.reduce((sum, slice) => sum + Number(slice.value), 0) || Number(data.total) || 0;
  const format = data.format;
  const isWide = size.width > size.height * 1.5 && size.width >= 320;
  const legendWidth = isWide ? Math.min(240, size.width * 0.45) : size.width;
  const chartWidth = isWide ? size.width - legendWidth - 16 : size.width;
  const legendRowCount = isWide ? 0 : Math.ceil(slices.length / Math.max(1, Math.floor(size.width / 150)));
  const chartHeight = isWide ? size.height : Math.max(60, size.height - legendRowCount * 22 - 8);
  const diameter = Math.max(40, Math.min(chartWidth, chartHeight) - 8);
  const outerRadius = diameter / 2;
  const innerRadius = outerRadius * 0.68;
  const centerX = chartWidth / 2;
  const centerY = chartHeight / 2;
  const share = (/** @type {number} */ value) => (total > 0 ? (Number(value) / total) * 100 : 0);
  let angle = 0;
  const hovered = hover ? slices.find((slice) => slice.key === hover.key) : null;
  // cada fatia com registros abre a lista; "Outros" abre as fatias dobradas
  const slicePoints = drill
    ? slices.flatMap((slice, index) => {
      const target = drill.target({ part: 'slice', key: slice.key });
      return target ? [{ id: slice.key, categoryIndex: index, seriesIndex: 0, href: target.href, label: slice.label, valueText: formatMetricValue(slice.value, format) }] : [];
    })
    : [];
  const pointByKey = new Map(slicePoints.map((point) => [point.id, point]));
  /** @param {string} key @param {Element} element @param {'keyboard' | 'touch'} source @param {string | null} [href] */
  const showAt = (key, element, source, href = null) => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const box = element.getBoundingClientRect();
    setHover({ key, x: box.left + box.width / 2 - bounds.left, y: box.top + box.height / 2 - bounds.top, source, href });
  };
  const navigation = useChartPointNavigation({
    points: slicePoints,
    label: drill?.widgetTitle ?? '',
    onFocusPoint: (point, element) => showAt(point.id, element, 'keyboard'),
    onBlurPoint: () => setHover((current) => (current?.source === 'keyboard' ? null : current)),
    onPin: (point, event) => {
      const bounds = containerRef.current?.getBoundingClientRect();
      if (bounds) setHover({ key: point.id, x: event.clientX - bounds.left, y: event.clientY - bounds.top, source: 'touch', href: point.href });
    },
    onUnpin: () => setHover(null),
  });

  return html`<div class="pie-chart" data-wide=${isWide ? 'true' : undefined} ref=${containerRef}>
    ${size.width > 0
      ? html`<svg width=${chartWidth} height=${chartHeight} class="chart-svg" ...${navigation.containerProps}>
          ${slices.length === 0
            ? html`<circle cx=${centerX} cy=${centerY} r=${(outerRadius + innerRadius) / 2} fill="none" stroke="var(--t-background-tertiary)" stroke-width=${outerRadius - innerRadius} />`
            : slices.map((slice) => {
                const start = angle;
                angle += (share(slice.value) / 100) * Math.PI * 2;
                const shape = html`<path
                  key=${slice.key}
                  class="chart-slice"
                  data-dimmed=${hover && hover.key !== slice.key ? 'true' : undefined}
                  d=${buildArcPath(centerX, centerY, outerRadius, innerRadius, start, angle)}
                  fill=${slice.color}
                  onPointerMove=${(/** @type {PointerEvent} */ event) => {
                    // a dica fixada pelo toque só sai com um toque fora do gráfico
                    if (event.pointerType === 'touch' || hover?.source === 'touch') return;
                    const bounds = containerRef.current?.getBoundingClientRect();
                    if (bounds) setHover({ key: slice.key, x: event.clientX - bounds.left, y: event.clientY - bounds.top, source: 'pointer' });
                  }}
                  onPointerLeave=${() => setHover((current) => (current?.source === 'touch' ? current : null))}
                />`;
                const point = pointByKey.get(slice.key);
                return point ? html`<a key=${slice.key} ...${navigation.pointProps(point, point)}>${shape}</a>` : shape;
              })}
          ${showCenterMetric && innerRadius > 24
            ? html`<g class="pie-center">
                <text class="pie-center-value" x=${centerX} y=${centerY - 2} text-anchor="middle" style=${{ fontSize: `${Math.max(12, Math.min(26, innerRadius / 2.6))}px` }}>
                  ${truncateToWidth(formatMetricValue(total, format, { compact: true }), innerRadius * 1.7, Math.max(12, Math.min(26, innerRadius / 2.6)))}
                </text>
                <text class="pie-center-label" x=${centerX} y=${centerY + Math.max(12, Math.min(18, innerRadius / 3.6))} text-anchor="middle">${t('dashboards.chart.total')}</text>
              </g>`
            : null}
        </svg>`
      : null}
    <ul class="pie-legend">
      ${slices.map((slice) => html`<li class="pie-legend-item" key=${slice.key} data-dimmed=${hover && hover.key !== slice.key ? 'true' : undefined}>
        <span class="chart-legend-swatch" style=${{ '--swatch-color': slice.color }}></span>
        <span class="pie-legend-label" title=${slice.label}>${slice.label}</span>
        ${isWide || showDataLabels
          ? html`<span class="pie-legend-value">${formatMetricValue(slice.value, format, { compact: true })}</span>
              <span class="pie-legend-share">${formatNumber(share(slice.value), { percentage: true, decimals: 0 })}</span>`
          : null}
      </li>`)}
    </ul>
    ${hovered && hover
      ? html`<${ChartTooltip}
          x=${hover.x}
          y=${hover.y - 10}
          containerWidth=${size.width}
          title=${hovered.label}
          rows=${[
            { key: 'value', label: t('dashboards.chart.value'), value: formatMetricValue(hovered.value, format), color: hovered.color },
            { key: 'share', label: t('dashboards.chart.share'), value: formatNumber(share(hovered.value), { percentage: true, decimals: 1 }) },
          ]}
          hint=${pointByKey.has(hovered.key) ? (hover.source === 'keyboard' ? 'keyboard' : 'pointer') : null}
          action=${hover.source === 'touch' && hover.href
            ? { href: hover.href, onClick: (/** @type {MouseEvent} */ event) => handleDrillLinkClick(event, /** @type {string} */ (hover.href)) }
            : null}
        />`
      : null}
  </div>`;
}
